import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BackdropLoader, toast } from '@livable/saas-react-components';
import { Button, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track, events } from 'src/configs/amplitude';
import { useStore } from 'src/stores';
import AttachBillFileModal from 'src/components/shared/AttachBillFileModal';
import { FilePreviewPlain } from 'src/components/shared/FilePreviewModal/plainIndex';
import { MAX_BILL_UPLOAD_MB } from 'src/stores/billAutomation/uploadBillFile';
import { billContentTypeOf } from 'src/utils/billFileType';
import { canDeleteRecoveredBill, getRecoveredBillDeleteHint } from './BillAutomationExtraction/billAutomationExtractionUtils';
import { FileVisibilitySetting } from './fileVisibilitySetting';
import useStyles from './styles';
import { useUploadedBillDelete } from './useUploadedBillDelete';
export const FileDetails = observer(_ref => {
  var _property$allocations, _property$allocations2, _property$allocations4, _property$allocations5, _property$allocations6, _utility$bill3, _property$allocations23;
  let onFileVisibilitySettingVisibleChange = _ref.onFileVisibilitySettingVisibleChange;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'Apply Billing';
  const currentCompany = store.homeDashboardStore.getCurrentCompany || {};
  const applyBill = currentCompany.billingTab.applyBill || {};
  const billingCycleId = applyBill.billingCycleId;
  const billingTab = currentCompany.billingTab;
  const isStatementSent = applyBill.status === 'TableReadyStatementSent';
  const isAllPropertiesMode = store.homeDashboardStore.selectedPropertyId === 'all';
  const property = isAllPropertiesMode ? store.homeDashboardStore.getCurrentCompanyProperties.find(p => p.id === applyBill.propertyId) || {} : store.homeDashboardStore.getCurrentProperty || {};
  const propertyId = property.id;
  const uploadedBillDelete = useUploadedBillDelete({
    property
  });
  const utility = (_property$allocations = property.allocations) === null || _property$allocations === void 0 ? void 0 : _property$allocations.utilityById.get(billingCycleId + applyBill.utilityId);
  const utilityTable = ((_property$allocations2 = property.allocations) === null || _property$allocations2 === void 0 || (_property$allocations2 = _property$allocations2.billingCycles.get(billingCycleId)) === null || _property$allocations2 === void 0 ? void 0 : _property$allocations2.utilityTables.find(i => i.utilityId === applyBill.utilityId)) || {};
  const disabled = isStatementSent || !utilityTable.isEditable;
  const isBillExtractionEnabled = store.account.featureFlagsStore.getIsBillExtractionEnabled;
  const getFileId = () => {
    var _property$allocations3, _utility$bill, _utility$bill2;
    if ((_property$allocations3 = property.allocations) !== null && _property$allocations3 !== void 0 && (_property$allocations3 = _property$allocations3.tempFileData) !== null && _property$allocations3 !== void 0 && _property$allocations3.fileId) return property.allocations.tempFileData.fileId;
    if (utility !== null && utility !== void 0 && (_utility$bill = utility.bill) !== null && _utility$bill !== void 0 && _utility$bill.redactedFileId) return utility.bill.redactedFileId;
    if (utility !== null && utility !== void 0 && (_utility$bill2 = utility.bill) !== null && _utility$bill2 !== void 0 && _utility$bill2.fileId) return utility.bill.fileId;
    return '';
  };
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFileLoading = _useState2[0],
    setIsFileLoading = _useState2[1];
  // The preview, and the file it was made for. Shown only for that file: one left over from a file the screen let go of --
  // the copy of an allocation's bill removed here (LB-6028), say -- must not stand in for the next file while that file's
  // own preview loads, or for good if loading it fails.
  const _useState3 = useState({
      fileId: '',
      link: ''
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    preview = _useState4[0],
    setPreview = _useState4[1];
  const filePreviewLink = preview.fileId && preview.fileId === getFileId() ? preview.link : '';
  const setFilePreviewLink = function (link) {
    let fileId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    // A preview that arrives for a file no longer on screen is dropped, not left to replace the current file's.
    if (link && fileId !== getFileId()) return;
    setPreview(link && fileId ? {
      fileId,
      link
    } : {
      fileId: '',
      link: ''
    });
  };
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    fileName = _useState6[0],
    setFileName = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    fileSize = _useState8[0],
    setFileSize = _useState8[1];
  const fileInputRef = useRef(null);
  const postedBillAutomationFileId = useRef('');
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isFileDragOver = _useState0[0],
    setIsFileDragOver = _useState0[1];

  // ── A bill recovered for this cycle (LB-5822) ────────────────────────────────────────────────────
  // Its document lives in bill-automation, not the file service, so there is no fileId to preview by.

  const recoveredBill = (_property$allocations4 = (_property$allocations5 = property.allocations) === null || _property$allocations5 === void 0 || (_property$allocations5 = _property$allocations5.tempFileData) === null || _property$allocations5 === void 0 ? void 0 : _property$allocations5.billAutomationResponse) !== null && _property$allocations4 !== void 0 ? _property$allocations4 : null;
  const hasRecoveredBill = !getFileId() && !!(recoveredBill !== null && recoveredBill !== void 0 && recoveredBill.billId) && !((_property$allocations6 = property.allocations) !== null && _property$allocations6 !== void 0 && (_property$allocations6 = _property$allocations6.tempFileData) !== null && _property$allocations6 !== void 0 && _property$allocations6.isDeleted);
  const _useState1 = useState({
      status: 'loading',
      url: ''
    }),
    _useState10 = _slicedToArray(_useState1, 2),
    recoveredPreview = _useState10[0],
    setRecoveredPreview = _useState10[1];
  useEffect(() => {
    if (!hasRecoveredBill) {
      setRecoveredPreview({
        status: 'loading',
        url: ''
      });
      return undefined;
    }
    let active = true;
    setRecoveredPreview({
      status: 'loading',
      url: ''
    });
    (async _resp$response => {
      // Fetched on open, never cached: the URL is presigned and expires in about fifteen minutes.
      const resp = await store.companyBills.getBillFileUrlAsync.run({
        billId: recoveredBill.billId
      });
      if (!active) return;
      const url = resp && resp.isSuccessful !== false ? (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.url : '';
      setRecoveredPreview(url ? {
        status: 'ready',
        url
      } : {
        status: 'none',
        url: ''
      });
    })();
    return () => {
      active = false;
    };
  }, [hasRecoveredBill, recoveredBill === null || recoveredBill === void 0 ? void 0 : recoveredBill.billId, store.companyBills]);

  // Only once the preview resolves: "none" means there is no document behind this bill, and
  // offering to share nothing is worse than offering nothing.
  //
  // And only for the bill the allocation names (LB-5755). Sharing is set on the allocation, so for one
  // uploaded a moment ago and not yet saved, the tick would share the document it is replacing instead.
  // It appears once the save has linked this one.
  const canShareRecoveredBill = hasRecoveredBill && recoveredPreview.status === 'ready' && !!(recoveredBill !== null && recoveredBill !== void 0 && recoveredBill.billId) && (utility === null || utility === void 0 || (_utility$bill3 = utility.bill) === null || _utility$bill3 === void 0 ? void 0 : _utility$bill3.sourceBillId) === recoveredBill.billId;

  // Read by the parent to make room for the control beside the utility switcher, so it has to
  // count BOTH ways the control can be drawn or the switcher sits on top of it.
  const isFileVisibilitySettingVisible = Boolean(getFileId() && filePreviewLink) || canShareRecoveredBill;
  useEffect(() => {
    onFileVisibilitySettingVisibleChange === null || onFileVisibilitySettingVisibleChange === void 0 || onFileVisibilitySettingVisibleChange(isFileVisibilitySettingVisible);
    return () => {
      onFileVisibilitySettingVisibleChange === null || onFileVisibilitySettingVisibleChange === void 0 || onFileVisibilitySettingVisibleChange(false);
    };
  }, [isFileVisibilitySettingVisible, onFileVisibilitySettingVisibleChange]);
  const getFileInfo = async data => {
    setIsFileLoading(true);
    const resp = await store.files.getFileInfoAsync.run(data);
    return resp;
  };
  const getFileBlobForBillAutomation = async fileId => {
    if (!fileId) return null;
    if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
      const filePreviewLinkResp = await store.files.getPresignedFileDownloadUrlAsync.run({
        fileId,
        isPreview: false
      });
      if (filePreviewLinkResp !== null && filePreviewLinkResp !== void 0 && filePreviewLinkResp.isSuccessful && filePreviewLinkResp.response) {
        const fileResp = await fetch(filePreviewLinkResp.response);
        if (fileResp.ok) return fileResp.blob();
      }
      // Fallback to direct preview link when presigned URL is unavailable
    }
    const previewLink = store.files.getFilePreviewLink({
      fileId
    });
    const fileResp = await fetch(previewLink);
    if (!fileResp.ok) return null;
    return fileResp.blob();
  };
  const postBillForAutomation = async response => {
    var _response$response, _property$allocations7, _property$allocations8, _response$response2, _responseFileName$spl, _property$buildingInf, _property$dashboard;
    if (!store.account.featureFlagsStore.getIsBillExtractionEnabled) return;
    const fileId = response === null || response === void 0 || (_response$response = response.response) === null || _response$response === void 0 ? void 0 : _response$response.fileId;
    if (!fileId || !propertyId) return;
    if (postedBillAutomationFileId.current === fileId) return;
    // Claimed before the first await, and let go of only if the send does not land. Choosing a file in
    // AttachBillFileModal reaches here twice at once -- its upload callback and isFileChosen -- and
    // marking it only after a successful send let both go out, the second landing as a Duplicate.
    postedBillAutomationFileId.current = fileId;

    // Said before the bill is let go of, and not taken back until a new one has actually arrived.
    // Until then the screen holds no bill while the allocation still names the one being replaced,
    // and anything that went looking would fetch it straight back (LB-5898).
    //
    // It covers the download below as well as the upload -- the gap opens here, not at the moment a
    // request is finally made -- and it survives a failure, because a replacement that did not land
    // leaves the new file on screen with nothing of its own to show, which is the one state that
    // must not be filled in with the bill it was replacing.
    (_property$allocations7 = property.allocations) === null || _property$allocations7 === void 0 || _property$allocations7.setTempFileData('hasUnfinishedReplacement', true);

    // A new file replaces the last one's extraction right away. Until this file's own result arrives, the
    // previous document's validated response would otherwise stay live behind the new filename — with its
    // Push button enabled and its bill id the one that gets pushed — and it would stay that way for good if
    // the start-bill call below failed.
    (_property$allocations8 = property.allocations) === null || _property$allocations8 === void 0 || _property$allocations8.setTempFileData('billAutomationResponse', null);
    const blob = await getFileBlobForBillAutomation(fileId);
    if (!blob) {
      postedBillAutomationFileId.current = '';
      return;
    }
    const responseFileName = (response === null || response === void 0 || (_response$response2 = response.response) === null || _response$response2 === void 0 ? void 0 : _response$response2.fileName) || 'bill-upload';
    const extension = (_responseFileName$spl = responseFileName.split('.').pop()) === null || _responseFileName$spl === void 0 ? void 0 : _responseFileName$spl.toLowerCase();
    const extensionToMime = {
      pdf: 'application/pdf',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp'
    };
    const allowedMimes = Object.values(extensionToMime);
    const mimeType = blob.type && allowedMimes.includes(blob.type) ? blob.type : extensionToMime[extension] || 'application/pdf';
    const file = new File([blob], responseFileName, {
      type: mimeType
    });
    const utilityInfo = store.utilityProviders.utilities.get(applyBill.utilityId) || {};
    const propertyAddress = applyBill.buildingAddress || ((_property$buildingInf = property.buildingInformation) === null || _property$buildingInf === void 0 ? void 0 : _property$buildingInf.buildingAddress) || ((_property$dashboard = property.dashboard) === null || _property$dashboard === void 0 ? void 0 : _property$dashboard.propertyAddress) || '';
    const automationResp = await store.billAutomationBillsApi.startBillAsync.run({
      file,
      propertyId,
      utilityNames: utilityInfo.providerName || undefined,
      propertyAddress: propertyAddress || undefined,
      requiredUtilities: utilityInfo.utilityTypeName || undefined,
      // What this upload is for, so the bill can be recovered while it is still being read — and
      // still if reading it fails, which resolves nothing to file it under.
      billingCycleId: billingCycleId || undefined,
      utilityId: applyBill.utilityId || undefined
    });
    if ((automationResp === null || automationResp === void 0 ? void 0 : automationResp.isSuccessful) === false && automationResp !== null && automationResp !== void 0 && automationResp.message) {
      toast('error', {
        body: [automationResp.message]
      });
    }
    if (!(automationResp !== null && automationResp !== void 0 && automationResp.isSuccessful) || !(automationResp !== null && automationResp !== void 0 && automationResp.response)) {
      // Not sent, so it may be sent again.
      postedBillAutomationFileId.current = '';
    }
    if (automationResp !== null && automationResp !== void 0 && automationResp.isSuccessful && automationResp !== null && automationResp !== void 0 && automationResp.response) {
      var _property$allocations9, _property$allocations0, _property$allocations1;
      (_property$allocations9 = property.allocations) === null || _property$allocations9 === void 0 || _property$allocations9.setTempFileData('billAutomationResponse', automationResp.response);
      // Cleared alongside: this bill was uploaded just now, so nothing about it was left unfinished.
      // Only the recovery sets the flag, and only a reset has cleared it until now — which today is
      // enough, because a bill on screen hides the upload area. It stops being enough the moment
      // uploading over a recovered bill is allowed, and the banner would then say something untrue.
      (_property$allocations0 = property.allocations) === null || _property$allocations0 === void 0 || _property$allocations0.setTempFileData('wasRecovered', null);
      // Only here, and deliberately not in a finally. A replacement that failed has not finished --
      // it has left the new file on screen with no extraction, and letting go of the flag would have
      // recovery put the replaced bill's extraction there instead.
      (_property$allocations1 = property.allocations) === null || _property$allocations1 === void 0 || _property$allocations1.setTempFileData('hasUnfinishedReplacement', false);
    }
  };

  /**
   * @param postForAutomation whether the file was attached just now and so is to be read. A file that
   *   was already attached when the screen opened is not sent again: the service has it, BillDetails
   *   recovers the cycle's bill from there, and sending it again landed another Duplicate bill every
   *   time the modal was opened -- the "already on file" banner, about the person's own bill (LB-5919).
   */
  const fileCallback = async function (response) {
    let _ref2 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref2$postForAutomati = _ref2.postForAutomation,
      postForAutomation = _ref2$postForAutomati === void 0 ? false : _ref2$postForAutomati;
    if (response) {
      var _response$response3;
      setFileName(response.response.fileName);
      setFileSize((_response$response3 = response.response) === null || _response$response3 === void 0 ? void 0 : _response$response3.fileSizeMB);
      if (postForAutomation) {
        await postBillForAutomation(response).catch(() => {
          // A send that threw did not land either, so the same file may be tried again.
          postedBillAutomationFileId.current = '';
        });
      }
      if (store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled) {
        var _response$response4;
        const fileNamePartials = (_response$response4 = response.response) === null || _response$response4 === void 0 ? void 0 : _response$response4.fileName.split('.');
        if (['doc', 'docx'].includes(fileNamePartials[fileNamePartials.length - 1])) {
          const requestFilePreviewLinkResp = await store.files.getRequestPreviewLinkAsync.run({
            fileId: response.response.fileId
          });
          if (requestFilePreviewLinkResp !== null && requestFilePreviewLinkResp !== void 0 && requestFilePreviewLinkResp.isSuccessful) {
            setFilePreviewLink(requestFilePreviewLinkResp.response, response.response.fileId);
          }
        } else {
          const filePreviewLinkResp = await store.files.getPresignedFileDownloadUrlAsync.run({
            fileId: response.response.fileId,
            isPreview: true
          });
          if (filePreviewLinkResp !== null && filePreviewLinkResp !== void 0 && filePreviewLinkResp.isSuccessful) {
            setFilePreviewLink(filePreviewLinkResp.response, response.response.fileId);
          }
        }
      } else {
        var _response$response5;
        setFilePreviewLink(store.files.getFilePreviewLink({
          fileId: response.response.fileId
        }, false, ".".concat((_response$response5 = response.response) === null || _response$response5 === void 0 || (_response$response5 = _response$response5.fileName) === null || _response$response5 === void 0 || (_response$response5 = _response$response5.split('.').pop()) === null || _response$response5 === void 0 ? void 0 : _response$response5.toLowerCase())), response.response.fileId);
      }
    }
    if (response === null) {
      setFileName('');
      setFilePreviewLink('');
    }
    setIsFileLoading(false);
  };

  // Opening on a file attached earlier: shown, never sent again.
  useEffect(() => {
    if (getFileId()) getFileInfo({
      fileId: getFileId()
    }).then(response => fileCallback(response));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [utility]);
  const addToPreviewQueue = () => {
    var _store$homeDashboardS;
    return billingTab.addToPreviewQueue({
      billingCycleId,
      id: billingCycleId + propertyId,
      portfolioId: ((_store$homeDashboardS = store.homeDashboardStore.getCurrentPortfolio) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.id) || '',
      propertyId
    });
  };

  /**
   * The bill the allocation on screen was made from, as it stands (LB-5952): `none` when it names none, `onFile` with
   * the bill, `gone`, or `unknown` when bill-automation could not say. The one on screen when it is held; otherwise --
   * its fetch still in flight, or one that failed -- asked for again, so nothing is decided on a bill merely not shown.
   * `wasUploadedHere` for a bill uploaded on this screen a moment ago rather than brought back.
   */
  const lookUpNamedBill = async () => {
    var _utility$bill4, _check$error;
    const namedBillId = utility !== null && utility !== void 0 && (_utility$bill4 = utility.bill) !== null && _utility$bill4 !== void 0 && _utility$bill4.id ? utility.bill.sourceBillId : null;
    if (!namedBillId) return {
      state: 'none'
    };
    if ((recoveredBill === null || recoveredBill === void 0 ? void 0 : recoveredBill.billId) === namedBillId) {
      var _property$allocations10;
      return {
        state: 'onFile',
        bill: recoveredBill,
        wasUploadedHere: !((_property$allocations10 = property.allocations) !== null && _property$allocations10 !== void 0 && (_property$allocations10 = _property$allocations10.tempFileData) !== null && _property$allocations10 !== void 0 && _property$allocations10.wasRecovered)
      };
    }
    const check = store.billAutomationBillsApi.getNamedBillOnFileAsync;
    const resp = await check.run(namedBillId);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && resp !== null && resp !== void 0 && resp.response) return {
      state: 'onFile',
      bill: resp.response,
      wasUploadedHere: false
    };
    if (((_check$error = check.error) === null || _check$error === void 0 ? void 0 : _check$error.status) === 404) return {
      state: 'gone'
    };
    return {
      state: 'unknown'
    };
  };
  const COULD_NOT_CHECK_NAMED_BILL = 'We couldn’t check the bill this allocation was made from. Try again.';
  const deleteFile = async () => {
    var _utility$bill5, _property$allocations11, _utility$bill6;
    track(events.fileRemoved('Bill'), {
      page,
      utility: applyBill.utilityName
    });

    // The copy of the bill the allocation was made from is that bill (LB-5952): deleting it deletes the bill, and
    // the allocation is archived with it. The copy stays with the archived allocation. Taking the file off alone
    // would leave the allocation made from a bill whose document comes straight back. Not a redaction's delete,
    // which only takes the redaction off: the bill it was made from is still the document, unredacted. An
    // allocation whose bill is already gone has only its file left to delete.
    const isRedaction = !!(utility !== null && utility !== void 0 && (_utility$bill5 = utility.bill) !== null && _utility$bill5 !== void 0 && _utility$bill5.redactedFileId) || !!((_property$allocations11 = property.allocations) !== null && _property$allocations11 !== void 0 && (_property$allocations11 = _property$allocations11.tempFileData) !== null && _property$allocations11 !== void 0 && _property$allocations11.isReducted);
    const named = isRedaction ? {
      state: 'none'
    } : await lookUpNamedBill();
    if (named.state === 'unknown') {
      toast('error', {
        title: 'File not deleted',
        body: [COULD_NOT_CHECK_NAMED_BILL]
      });
      return;
    }
    if (named.state === 'onFile') {
      const allocatedBill = named.bill;
      // Uploaded on this screen a moment ago, it is this screen's to delete; brought back, it is only when it was
      // uploaded here for this utility. Never once billed, nor once its billing month has closed (LB-5952).
      const isClosed = utilityTable.isEditable === false;
      const deleteOptions = {
        allocatedSourceBillId: allocatedBill.billId,
        isBilled: isStatementSent,
        isClosed
      };
      const mayDelete = named.wasUploadedHere ? !isStatementSent && !isClosed : canDeleteRecoveredBill(allocatedBill, applyBill.utilityId, deleteOptions);
      if (!mayDelete) {
        toast('error', {
          title: 'Bill not deleted',
          body: [getRecoveredBillDeleteHint(false, {
            isAllocatedHere: true,
            isBilled: isStatementSent,
            isClosed
          })]
        });
        return;
      }
      uploadedBillDelete.request(allocatedBill.billId, {
        isAllocatedHere: true
      });
      return;
    }
    const fileId = getFileId();
    if (utility !== null && utility !== void 0 && (_utility$bill6 = utility.bill) !== null && _utility$bill6 !== void 0 && _utility$bill6.id) {
      var _utility$bill7, _utility$bill8, _utility$bill9, _utility$bill0, _property$allocations12, _property$allocations13;
      // The bill lets go of the file first, and the file is deleted only once it has. Refused -- the table can no
      // longer change, its month closed or a batch locked it while this was open (LB-5952) -- the bill keeps its file,
      // and nothing is deleted from under it. The refusal is said by the request itself.
      const detached = await store.allocations.getFileAttachedAsync.run({
        fileId: null,
        isShared: false,
        billId: (_utility$bill7 = utility.bill) === null || _utility$bill7 === void 0 ? void 0 : _utility$bill7.id
      });
      if (!(detached !== null && detached !== void 0 && detached.isSuccessful)) return;
      await addToPreviewQueue();
      utility === null || utility === void 0 || (_utility$bill8 = utility.bill) === null || _utility$bill8 === void 0 || _utility$bill8.changeRedactedFileId(null);
      utility === null || utility === void 0 || (_utility$bill9 = utility.bill) === null || _utility$bill9 === void 0 || _utility$bill9.changeIsSharedFile(false);
      utility === null || utility === void 0 || (_utility$bill0 = utility.bill) === null || _utility$bill0 === void 0 || _utility$bill0.changeFileId(null);
      (_property$allocations12 = property.allocations) === null || _property$allocations12 === void 0 || (_property$allocations12 = _property$allocations12.billingCycles.get(billingCycleId)) === null || _property$allocations12 === void 0 || (_property$allocations12 = _property$allocations12.utilityTables.find(i => i.utilityId === applyBill.utilityId)) === null || _property$allocations12 === void 0 || _property$allocations12.changeRedactedFileId(null);
      (_property$allocations13 = property.allocations) === null || _property$allocations13 === void 0 || (_property$allocations13 = _property$allocations13.billingCycles.get(billingCycleId)) === null || _property$allocations13 === void 0 || (_property$allocations13 = _property$allocations13.utilityTables.find(i => i.utilityId === applyBill.utilityId)) === null || _property$allocations13 === void 0 || _property$allocations13.changeFileId(null);
    }
    await store.files.deleteFileAsync.run({
      fileId
    });
    property.allocations.setTempFileData('fileId', '');
    property.allocations.setTempFileData('isDeleted', true);
    property.allocations.setTempFileData('isReducted', false);
    property.allocations.setTempFileData('billAutomationResponse', null);
    fileCallback(null);
  };

  /**
   * @param postForAutomation whether to send the file on to be read. Not for an upload that went to
   *   bill-automation itself a moment ago: it has the file already.
   * @param sourceBillId the bill-automation bill this file is the copy of, when there is one. Attached in the
   *   same call as the file, so an allocation bill that already exists records the two together (LB-5755) and
   *   nothing has to guess later which bill its file came from.
   */
  const uploadFile = async function (id) {
    var _utility$bill1;
    let _ref3 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref3$postForAutomati = _ref3.postForAutomation,
      postForAutomation = _ref3$postForAutomati === void 0 ? true : _ref3$postForAutomati,
      sourceBillId = _ref3.sourceBillId;
    property.allocations.setTempFileData('fileId', id);
    property.allocations.setTempFileData('isDeleted', false);
    property.allocations.setTempFileData('isReducted', false);
    if (utility !== null && utility !== void 0 && (_utility$bill1 = utility.bill) !== null && _utility$bill1 !== void 0 && _utility$bill1.id) {
      var _utility$bill10, _property$allocations14, _utility$bill11;
      // An allocation with a bill of its own gets no other: it lets go of one only by that bill being deleted, which
      // archives it (LB-5952). What is linked here is a first bill, or one in place of a bill already gone.
      const linksSourceBill = !!sourceBillId && sourceBillId !== ((_utility$bill10 = utility.bill) === null || _utility$bill10 === void 0 ? void 0 : _utility$bill10.sourceBillId);
      await store.allocations.getFileAttachedAsync.run(_objectSpread({
        fileId: id,
        isShared: (_property$allocations14 = property.allocations) === null || _property$allocations14 === void 0 || (_property$allocations14 = _property$allocations14.tempFileData) === null || _property$allocations14 === void 0 ? void 0 : _property$allocations14.isShared,
        billId: (_utility$bill11 = utility.bill) === null || _utility$bill11 === void 0 ? void 0 : _utility$bill11.id
      }, linksSourceBill ? {
        sourceBillId
      } : {})).then(response => {
        var _utility$bill12, _utility$bill13, _utility$bill13$chang, _property$allocations15;
        utility === null || utility === void 0 || (_utility$bill12 = utility.bill) === null || _utility$bill12 === void 0 || _utility$bill12.changeFileId(id);
        // Said here too, so the save that follows knows the link is made and does not send it again.
        if (linksSourceBill && response !== null && response !== void 0 && response.isSuccessful) utility === null || utility === void 0 || (_utility$bill13 = utility.bill) === null || _utility$bill13 === void 0 || (_utility$bill13$chang = _utility$bill13.changeSourceBillId) === null || _utility$bill13$chang === void 0 || _utility$bill13$chang.call(_utility$bill13, sourceBillId);
        (_property$allocations15 = property.allocations) === null || _property$allocations15 === void 0 || (_property$allocations15 = _property$allocations15.billingCycles.get(billingCycleId)) === null || _property$allocations15 === void 0 || (_property$allocations15 = _property$allocations15.utilityTables.find(i => i.utilityId === applyBill.utilityId)) === null || _property$allocations15 === void 0 || _property$allocations15.changeFileId(id);
      }).then(addToPreviewQueue);
    }
    await store.files.getFilesInfoAsync.run({
      fileIds: [id]
    });
    await getFileInfo({
      fileId: id
    }).then(response => fileCallback(response, {
      postForAutomation
    }));
  };
  const dropFileUploaderCallback = async id => {
    if (id === null) {
      await deleteFile();
    } else {
      await uploadFile(id);
    }
  };

  /**
   * Whether another bill may be attached to the allocation on screen (LB-5952). Not while the bill it was made from is
   * still on file: an allocation lets go of its bill only by that bill being deleted, which archives it with it.
   * Attached beside that bill, a file would be taken for its copy, and deleting it would delete the bill; read, it
   * would move the allocation onto itself, out from under the bill bill-automation still counts it against.
   *
   * Only reached with that bill off the screen -- while it is being fetched, or after a fetch that failed -- and a
   * check that cannot answer refuses rather than guesses.
   */
  const mayAttachAnotherBill = async () => {
    const named = await lookUpNamedBill();
    if (named.state === 'none' || named.state === 'gone') return true;
    toast('error', {
      body: [named.state === 'onFile' ? 'This allocation was made from a bill that is still on file. Delete that bill to allocate a different one.' : COULD_NOT_CHECK_NAMED_BILL]
    });
    return false;
  };

  /** A file chosen from the property's earlier bills, attached the way an upload is: read, so asked about first. */
  const attachChosenFile = async id => {
    if (id !== null && !(await mayAttachAnotherBill())) return;
    await dropFileUploaderCallback(id);
  };

  // A file chosen from the property's earlier bills by its row's own action, which puts it on screen itself rather than
  // through attachChosenFile: held to the same, and taken off again if refused. One that came through attachChosenFile
  // is not on screen yet here -- it is attached, and sent, once let on.
  useEffect(() => {
    if (!currentCompany.billingTab.isFileChosen) return;
    currentCompany.billingTab.setIsFileChosen(false);
    currentCompany.billingTab.setIsPreviewCycleChargesModalOpen(false);
    const chosenFileId = getFileId();
    if (!chosenFileId) return;
    (async () => {
      if (!(await mayAttachAnotherBill())) {
        var _property$allocations16;
        if (((_property$allocations16 = property.allocations) === null || _property$allocations16 === void 0 || (_property$allocations16 = _property$allocations16.tempFileData) === null || _property$allocations16 === void 0 ? void 0 : _property$allocations16.fileId) === chosenFileId) property.allocations.setTempFileData('fileId', '');
        return;
      }
      getFileInfo({
        fileId: chosenFileId
      }).then(response => fileCallback(response, {
        postForAutomation: true
      }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany.billingTab.isFileChosen]);
  const dropFileUploaderCallbackRef = useRef(dropFileUploaderCallback);
  dropFileUploaderCallbackRef.current = dropFileUploaderCallback;

  /** The copy in the file service, the way the file service has always been sent a file here. */
  const uploadFileServiceCopy = async pickedFile => store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled ? store.files.uploadFileViaPresignedUrlExperimentAsync.run({
    file: pickedFile,
    createdBy: store.account.userProfile.id
  }) : store.files.uploadFileAsync.run({
    file: pickedFile
  });

  /**
   * The copy's id, tried a few times over. Once bill-automation has read the file, asking the person to upload
   * it again for a copy would not work: the same bytes land there as a Duplicate of the bill just read. So a
   * failure that may pass is retried here, with the same file, before anything is said -- and through the
   * silent uploads, so an attempt that fails and is then recovered from shows no error.
   */
  const COPY_ATTEMPTS = 3;
  const uploadFileServiceCopySilently = async pickedFile => store.account.featureFlagsStore.getIsPresignedFileFeatureEnabled ? store.files.uploadFileViaPresignedUrlSilentAsync.run({
    file: pickedFile,
    createdBy: store.account.userProfile.id
  }) : store.files.uploadFileSilentAsync.run({
    file: pickedFile
  });
  const uploadFileServiceCopyId = async pickedFile => {
    for (let attempt = 1; attempt <= COPY_ATTEMPTS; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const copy = await uploadFileServiceCopySilently(pickedFile);
        if (copy !== null && copy !== void 0 && copy.isSuccessful && copy.response) return copy.response;
      } catch (_unused) {
        // tried again below
      }
    }
    return null;
  };

  /**
   * Sends a picked bill to bill-automation to be read, and keeps a copy of it in the file service.
   *
   * Both, not one. Bill-automation reads it (LB-5898 moved that upload straight to its bucket, which lifted it
   * past the ~4.5 MB a request through the API can carry). The file-service copy is what everything else here
   * already stands on: the preview a person can redact before residents see it, "Use previous bills", the
   * property's Files page, and the resident portal's own list. Without it every one of those stopped working
   * for a bill uploaded here, which the system could do before that change.
   *
   * @param canRead whether bill-automation can read this file at all: a type it knows, within its size. A file
   *   it cannot read is still attached, as it always was, and the person enters the bill themselves.
   */
  const uploadToBillBucket = async function (pickedFile, contentType, source) {
    var _property$allocations20, _property$allocations21, _property$allocations22;
    let _ref4 = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {},
      _ref4$canRead = _ref4.canRead,
      canRead = _ref4$canRead === void 0 ? true : _ref4$canRead;
    let bill = null;
    if (canRead) {
      var _property$buildingInf2, _property$dashboard2, _uploaded, _uploaded2;
      const utilityInfo = store.utilityProviders.utilities.get(applyBill.utilityId) || {};
      const propertyAddress = applyBill.buildingAddress || ((_property$buildingInf2 = property.buildingInformation) === null || _property$buildingInf2 === void 0 ? void 0 : _property$buildingInf2.buildingAddress) || ((_property$dashboard2 = property.dashboard) === null || _property$dashboard2 === void 0 ? void 0 : _property$dashboard2.propertyAddress) || '';
      let uploaded = null;
      try {
        uploaded = await store.billAutomationBillsApi.uploadBillAsync.run({
          file: pickedFile,
          contentType,
          channel: 'Manual',
          propertyId,
          // What this upload is for, so the bill can be found again while it is still being read --
          // and still if reading it fails, which resolves nothing to file it under.
          billingCycleId: billingCycleId || undefined,
          utilityId: applyBill.utilityId || undefined,
          utilityNames: utilityInfo.providerName || undefined,
          propertyAddress: propertyAddress || undefined,
          requiredUtilities: utilityInfo.utilityTypeName || undefined
        });
      } catch (_unused2) {
        // Not read, but it is still attached below: the copy does not depend on this, and the person enters
        // the bill themselves, as they did before bills were read here at all.
        uploaded = null;
      }

      // The service's own reason when it refused (a property this person may not put a bill on): nothing is
      // attached, because the same reason stands against allocating it here.
      if ((_uploaded = uploaded) !== null && _uploaded !== void 0 && _uploaded.refusal) {
        toast('error', {
          body: [uploaded.refusal]
        });
        return;
      }
      bill = (_uploaded2 = uploaded) !== null && _uploaded2 !== void 0 && (_uploaded2 = _uploaded2.bill) !== null && _uploaded2 !== void 0 && _uploaded2.billId ? uploaded.bill : null;
    }
    const copyFileId = await uploadFileServiceCopyId(pickedFile);
    if (!bill && !copyFileId) {
      toast('error', {
        body: ['We could not upload this bill. Try it again.']
      });
      return;
    }
    track(events.fileUploaded('Bill'), {
      page,
      source
    });
    if (bill) {
      var _property$allocations17, _property$allocations18, _property$allocations19;
      (_property$allocations17 = property.allocations) === null || _property$allocations17 === void 0 || _property$allocations17.setTempFileData('billAutomationResponse', bill);
      // Uploaded a moment ago, so there is nothing for the banner to call unfinished.
      (_property$allocations18 = property.allocations) === null || _property$allocations18 === void 0 || _property$allocations18.setTempFileData('wasRecovered', null);
      // A bill has arrived, so any replacement still outstanding has finished.
      (_property$allocations19 = property.allocations) === null || _property$allocations19 === void 0 || _property$allocations19.setTempFileData('hasUnfinishedReplacement', false);
    }
    if (copyFileId) {
      var _bill;
      await uploadFile(copyFileId, {
        postForAutomation: false,
        sourceBillId: (_bill = bill) === null || _bill === void 0 ? void 0 : _bill.billId
      });
      // Attached either way; when it was not read -- a file it cannot read, or a reading that did not start --
      // the details are the person's to enter, and they need telling so, since the reading upload is silent.
      if (!bill) {
        toast('info', {
          body: ['This bill couldn’t be read automatically, so it was attached as it is. Enter its details below.']
        });
      }
      return;
    }

    // Read, but not copied: the document is shown from bill-automation, as a recovered bill is, and saving still
    // links the allocation to it. It has no copy to redact or for the property's files. Not "upload it again":
    // the same file would land as a Duplicate and take the place of the bill just read.
    (_property$allocations20 = property.allocations) === null || _property$allocations20 === void 0 || _property$allocations20.setTempFileData('fileId', '');
    (_property$allocations21 = property.allocations) === null || _property$allocations21 === void 0 || _property$allocations21.setTempFileData('isDeleted', false);
    (_property$allocations22 = property.allocations) === null || _property$allocations22 === void 0 || _property$allocations22.setTempFileData('isReducted', false);
    toast('error', {
      body: ['The bill was read, but a copy for the property’s files could not be saved, so it can’t be redacted here. ' + 'Its details are below, and saving still attaches it.']
    });
  };

  /**
   * What the file service has always accepted here, and so what an upload here accepts: every upload keeps a
   * copy there. Bill-automation's own, lower ceiling only decides whether the file is also read.
   */
  const FILE_SERVICE_MAX_MB = 100;

  /** The path for a company that has bill extraction switched off: the file service, as before. */
  const uploadToFileService = async (pickedFile, source) => {
    const uploadedFileResult = await uploadFileServiceCopy(pickedFile);
    if (!(uploadedFileResult !== null && uploadedFileResult !== void 0 && uploadedFileResult.isSuccessful)) return;
    track(events.fileUploaded('Bill'), {
      page,
      source
    });
    await dropFileUploaderCallbackRef.current(uploadedFileResult.response);
  };

  // Held in a ref for the same reason dropFileUploaderCallback is: processBillFile is memoised, and
  // both of these close over the property and utility on screen. Captured directly, a render that
  // changed neither of the memo keys would leave the upload filing the bill under the last utility.
  const uploadBillRef = useRef(null);
  uploadBillRef.current = async (pickedFile, contentType, source, options) => {
    if (!(await mayAttachAnotherBill())) return;
    if (isBillExtractionEnabled) await uploadToBillBucket(pickedFile, contentType, source, options);else await uploadToFileService(pickedFile, source);
  };
  const processBillFile = useCallback(async function (pickedFile) {
    let _ref5 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      resetFileInput = _ref5.resetFileInput,
      source = _ref5.source;
    if (!pickedFile) return;

    // Every upload keeps its file-service copy, so the file service's ceiling is the one that refuses a file,
    // as it always has here. Bill-automation's is lower and only decides whether the file is also read.
    if (pickedFile.size > FILE_SERVICE_MAX_MB * 1048576) {
      toast('error', {
        body: ["File is too large, maximum file size is ".concat(FILE_SERVICE_MAX_MB, " MB")]
      });
      resetFileInput === null || resetFileInput === void 0 || resetFileInput();
      return;
    }

    // The bill-automation upload url is signed for a content type, so one has to be named before the file
    // moves. A file with none, or over its size, is attached without being read rather than refused.
    const contentType = billContentTypeOf(pickedFile);
    const canRead = !!contentType && pickedFile.size <= MAX_BILL_UPLOAD_MB * 1048576;
    setIsFileLoading(true);
    try {
      await uploadBillRef.current(pickedFile, contentType, source, {
        canRead
      });
    } catch (_unused3) {
      toast('error', {
        body: ['We could not upload this bill. Try it again.']
      });
    } finally {
      setIsFileLoading(false);
      resetFileInput === null || resetFileInput === void 0 || resetFileInput();
    }
  }, []);
  const handleDirectFileUpload = async event => {
    var _event$target;
    const pickedFile = event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 || (_event$target = _event$target.files) === null || _event$target === void 0 ? void 0 : _event$target[0];
    if (!pickedFile) return;
    const input = event === null || event === void 0 ? void 0 : event.target;
    await processBillFile(pickedFile, {
      resetFileInput: () => {
        if (input) input.value = '';
      },
      source: 'File Picker'
    });
  };
  const hasFileDrag = event => {
    var _event$dataTransfer;
    const types = event === null || event === void 0 || (_event$dataTransfer = event.dataTransfer) === null || _event$dataTransfer === void 0 ? void 0 : _event$dataTransfer.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  };
  const handleDropAreaDragEnter = useCallback(e => {
    if (disabled || !hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    // Ignore re-enters when moving between children inside the drop zone.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsFileDragOver(true);
  }, [disabled]);
  const handleDropAreaDragLeave = useCallback(e => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsFileDragOver(false);
  }, [disabled]);
  const handleDropAreaDragOver = useCallback(e => {
    if (disabled || !hasFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    try {
      e.dataTransfer.dropEffect = 'copy';
    } catch (_unused4) {
      /* ignore */
    }
  }, [disabled]);
  const handleDropAreaDrop = useCallback(async e => {
    var _e$dataTransfer;
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragOver(false);
    const file = (_e$dataTransfer = e.dataTransfer) === null || _e$dataTransfer === void 0 || (_e$dataTransfer = _e$dataTransfer.files) === null || _e$dataTransfer === void 0 ? void 0 : _e$dataTransfer[0];
    if (!file) return;
    await processBillFile(file, {
      source: 'Drag And Drop'
    });
  }, [disabled, processBillFile]);
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.modalEditor, " ").concat(!(getFileId() === '' || ((_property$allocations23 = property.allocations) === null || _property$allocations23 === void 0 || (_property$allocations23 = _property$allocations23.tempFileData) === null || _property$allocations23 === void 0 ? void 0 : _property$allocations23.fileId) === '') ? 'isFile' : '')
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: !!isFileLoading
  }), getFileId() === '' && !hasRecoveredBill ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.active, " ").concat(classes.dropArea, " ").concat(disabled ? 'disabled' : '', " ").concat(isFileDragOver ? classes.dropAreaFileDragOver : ''),
    onDragEnter: handleDropAreaDragEnter,
    onDragLeave: handleDropAreaDragLeave,
    onDragOver: handleDropAreaDragOver,
    onDrop: handleDropAreaDrop
  }, /*#__PURE__*/React.createElement(Typography, {
    component: "p",
    variant: "h4"
  }, isBillExtractionEnabled ? 'Upload your bill file and we will extract data for you' : 'Upload your bill file'), /*#__PURE__*/React.createElement(Button, {
    'custom-attribute': '07-attach-file',
    className: classes.attachFileButton,
    disabled: disabled,
    onClick: () => {
      var _fileInputRef$current;
      track(events.clicked('Attach Bill File'), {
        page,
        utility: applyBill.utilityName
      });
      (_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 || _fileInputRef$current.click();
    },
    size: "small",
    variant: "contained"
  }, "Attach bill file"), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    onChange: handleDirectFileUpload,
    style: {
      display: 'none'
    }
  }), /*#__PURE__*/React.createElement(Typography, {
    className: classes.attachFileControllersDivider,
    variant: "body2"
  }, t('or')), /*#__PURE__*/React.createElement("span", {
    'custom-attribute': '07-attach-file',
    className: classes.attachFileLinkButton,
    disabled: disabled,
    onClick: () => {
      track(events.modalOpened('Use Previous Bills'), {
        page,
        utility: applyBill.utilityName
      });
      currentCompany.billingTab.setIsAttachBillFileModalOpen(applyBill.utilityName);
    },
    size: "small",
    variant: "text"
  }, t('allocations.use_previous_bills'))), /*#__PURE__*/React.createElement(AttachBillFileModal, {
    disabled: disabled,
    dropFileUploaderCallback: attachChosenFile,
    property: property,
    setIsFileLoading: setIsFileLoading,
    utilityId: applyBill.utilityId
  })) : null, hasRecoveredBill ? /*#__PURE__*/React.createElement("div", {
    className: classes.recoveredFilePane
  }, recoveredPreview.status === 'ready' ?
  /*#__PURE__*/
  // Read in-page by the same viewer every other document here uses. Deliberately NOT an
  // iframe: the browser would render the PDF with its built-in viewer, which is a
  // force-installed extension, and on a managed profile whose DeveloperToolsAvailability
  // policy is left at its default that blocks DevTools for the whole tab. This needs the
  // bill bucket to allow the origin, which it now does (LB-5822).
  React.createElement(FilePreviewPlain, {
    docUri: recoveredPreview.url,
    fileId: "",
    fileName: (recoveredBill === null || recoveredBill === void 0 ? void 0 : recoveredBill.fileName) || 'Bill document',
    isDisabledParent: true,
    isPreviewOnly: true,
    key: recoveredPreview.url
  }) : /*#__PURE__*/React.createElement("div", {
    className: classes.recoveredFilePlaceholder
  }, recoveredPreview.status === 'none' ? 'No document is available to preview for this bill.' : 'Loading the bill document…')) : null, getFileId() && !filePreviewLink && !isFileLoading ?
  /*#__PURE__*/
  // Attached, but its preview could not be set up -- the file's details or its link did not load. Without
  // this the pane is empty: no preview, so no delete, and no upload area while a file is attached, which
  // leaves nothing here to remove or replace it with.
  React.createElement("div", {
    className: classes.unshownFilePlaceholder,
    'custom-attribute': 'unshown-bill-file'
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, "This bill\u2019s file couldn\u2019t be shown."), !disabled && /*#__PURE__*/React.createElement(Button, {
    onClick: async () => {
      await deleteFile();
    },
    size: "small",
    variant: "outlined"
  }, "Remove file")) : null, getFileId() && filePreviewLink && /*#__PURE__*/React.createElement(FilePreviewPlain, {
    handleDelete: async () => {
      await deleteFile();
    },
    key: getFileId() + filePreviewLink + Date.now(),
    isPreviewForm: true,
    docUri: filePreviewLink,
    fileName: fileName,
    fileSize: fileSize,
    isDisabledParent: false,
    fileId: getFileId()
    // Read-only whenever the table is: statements sent, or billed, locked or in a completed month. Its document is
    // part of the bill there, and property-service refuses the change (LB-5952); who may see it stays below.
    ,
    isPreviewOnly: disabled,
    uploadEditedFile: async newId => {
      setFilePreviewLink('');
      await uploadFile(newId);
    }
  }), getFileId() && filePreviewLink && /*#__PURE__*/React.createElement(FileVisibilitySetting, null), canShareRecoveredBill && /*#__PURE__*/React.createElement(FileVisibilitySetting, {
    isForRecoveredBill: true
  }), uploadedBillDelete.confirmation);
});